<template>
  <div class="stripe-charge-list-table">
    <el-table
      :data="rows"
      @sort-change="doChangeSort"
      ref="table"
      row-key="id"
      v-loading="loading"
    >
      <el-table-column
        :label="fields.createdAt.label"
        :prop="fields.createdAt.name"
        min-width="160"
      >
        <template slot-scope="scope">
          {{ presenter(scope.row, 'createdAt') }}
        </template>
      </el-table-column>

      <el-table-column
        :label="fields.stripeChargeId.label"
        :prop="fields.stripeChargeId.name"
        min-width="160"
      >
        <div
          slot-scope="scope"
          class="stripe-charge-list-table__id"
        >
          {{ presenter(scope.row, 'stripeChargeId') }}
        </div>
      </el-table-column>

      <el-table-column
        :label="fields.amount.label"
        :prop="fields.amount.name"
        min-width="160"  
      >
        <template slot-scope="scope">
          <app-i18n-amount
            :amount="scope.row.amount"
            :currency="scope.row.currency"
            currency-display="code"
          />
          <router-link :to="feeInfoRoute">
            <app-i18n-amount
              :amount="scope.row.stripeFee + scope.row.applicationFee"
              :currency="scope.row.currency"
              :fee="i18n('entities.stripeIncome.table.fee')"
              class="stripe-charge-list-table__amount__fee--small"
            />
          </router-link>
        </template>
      </el-table-column>

      <el-table-column
        :label="fields.status.label"
        :prop="fields.status.name"
        min-width="160"
      >
        <div
          class="stripe-charge-list-table__status"
          slot-scope="scope"
        >
          <i
            class="icon-radio-button-fill stripe-charge-list-table__status__icon"
            :style="getTagStyle('status', scope.row.status)"
          />
          <span
            class="stripe-charge-list-table__status__span"
            v-text="presenter(scope.row, 'status')"
          />
        </div>
      </el-table-column>

      <el-table-column
        :label="fields.paymentMethod.label"
        :prop="fields.paymentMethod.name"
        min-width="160"
      >
        <div
          slot-scope="scope"
          class="stripe-charge-list-table__card"
        >
          <img
            v-if="scope.row.paymentMethod && scope.row.paymentMethod.card && scope.row.paymentMethod.card.brand"
            :src="`/images/cards/${scope.row.paymentMethod.card.brand}.png`" 
            class="stripe-charge-list-table__card__img"
          />
          <img
            v-else
            src="/images/cards/default.png" 
            class="stripe-charge-list-table__card__img"
          />
          <span
            v-if="scope.row.paymentMethod && scope.row.paymentMethod.card && scope.row.paymentMethod.card.last4"
            class="stripe-charge-list-table__card__last4"
          >
            ****{{scope.row.paymentMethod.card.last4}}
          </span>
        </div>
      </el-table-column>

      <el-table-column
        :label="fields.user.label"
        :prop="fields.user.name"
        min-width="160"
      >
        <template slot-scope="scope">
          <span
            v-if="scope.row.user"
            class="stripe-charge-list-table__cell__span"
          >
            {{ scope.row.user.fullName }}
          </span>
        </template>
      </el-table-column>

    </el-table>

    <div class="el-pagination-wrapper">
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import { i18n } from '@shared/i18n';
import TagsFactory from '@shared/factories/tags-factory';

import { StripeChargeModel } from '@modules/stripe/stripe-charge/stripe-charge-model';
import { StripechargePermissions } from '@modules/stripe/stripe-charge/stripe-charge-permissions';

const { fields } = StripeChargeModel;

export default {
  name: 'app-stripe-charge-list-table',

  data() {
    return {
      i18n,
    }
  },

  computed: {
    ...mapGetters({
      rows: 'stripeCharge/list/rows',
      count: 'stripeCharge/list/count',
      loading: 'stripeCharge/list/loading',
      pagination: 'stripeCharge/list/pagination',
      currentUser: 'auth/currentUser',
      paginationLayout: 'layout/paginationLayout',
      currentManage: 'auth/manage/currentManage',
    }),

    hasPermissionToEdit() {
      return new StripechargePermissions(this.currentUser).edit;
    },

    fields() {
      return fields;
    },

    feeInfoRoute() {
      return {
        name: 'subpropertyFeeInfo',
        params: {
          id: this.currentManage.subproperty.id,
        },
      };
    },
  },

  methods: {
    ...mapActions({
      doChangeSort: 'stripeCharge/list/doChangeSort',
      doChangePaginationCurrentPage: 'stripeCharge/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize: 'stripeCharge/list/doChangePaginationPageSize',
      doMountTable: 'stripeCharge/list/doMountTable',
    }),

    presenter(row, fieldName) {
      return StripeChargeModel.presenter(row, fieldName);
    },

    getTagStyle(keyEnum, valueEnum) {
      return  new TagsFactory({
        entityName: 'stripeCharges'
      }).getTagStyle({
        keyEnum: keyEnum,
        valueEnum: valueEnum
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.stripe-charge-list-table {
  &__amount__fee--small {
    display: grid;
    font-size: 10px;
    text-transform: initial;
    color: rgba(125, 125, 125, 0.74);
    text-decoration: underline;
    text-decoration-style:dotted;
    line-height: 1.6;
  }

  &__id {
    font-size: 12px;
  }

  &__status {
    width: 120px;
    border-radius: 7px;
    text-transform: capitalize;
    box-shadow: 0 2px 5px 0 rgba(223, 223, 223, 0.7);
    font-size: 12px;
    letter-spacing: 0;
    padding: 10px 0px 6px 12px;

    display: grid;
    grid-template-areas: 'dot label';
    grid-template-columns: 20px;
    grid-template-rows: 20px;
    align-items: center; 
  }

  &__cell__span {
    padding-left: 24px;
  }

  &__card {
    display: grid;
    grid-template-areas: 'icon last4';
    grid-gap: 8px;
    align-items: center;
    grid-template-columns: 40px 1fr;

    &__img {
      width: 40px;
      grid-area: icon;
    }

    &__last4 {
      grid-area: last4;
      font-size: 12px;
    }
  }
}
</style>

<style>
body .stripe-charge-list-table .el-table__row {
  cursor: default;
}
</style>